import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Seo } from "@/components/Seo";
import { DemoBanner } from "@/components/portal/DemoBanner";
import { DocumentUploadCard } from "@/components/portal/DocumentUploadCard";
import { MyDocumentsCard } from "@/components/portal/MyDocumentsCard";
import {
  DRIVER_STATUS_OPTIONS,
  listLoads,
  updateLoadStatus,
  type LoadRecord,
} from "@/features/loads/repo";
import { useAuth } from "@/features/auth/useAuth";

export default function DriverDashboard() {
  const { user } = useAuth();
  const [loads, setLoads] = useState<LoadRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [documentsRefreshKey, setDocumentsRefreshKey] = useState(0);
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    listLoads(user)
      .then(setLoads)
      .catch(() => setLoads([]))
      .finally(() => setLoading(false));
  }, [user]);

  const handleStatusChange = async (loadId: string, status: LoadRecord["status"]) => {
    setSavingId(loadId);
    try {
      await updateLoadStatus(loadId, status);
      setLoads((current) =>
        current.map((load) => (load.id === loadId ? { ...load, status } : load)),
      );
    } finally {
      setSavingId(null);
    }
  };

  return (
    <>
      <Seo title="Driver portal" noindex />
      <DemoBanner />
      <h1 className="text-2xl font-bold">My loads</h1>
      <p className="mt-1 text-sm text-brand-500">
        Update status and upload BOL/POD. Mobile-first; installable to your home screen.
      </p>

      {loading && <p className="mt-6 text-sm text-brand-500">Loading assigned loads...</p>}

      {!loading && loads.length === 0 && (
        <p className="mt-6 text-sm text-brand-500">No loads assigned right now.</p>
      )}

      <div className="mt-6 grid gap-4">
        {loads.map((load) => (
          <Card key={load.id}>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="break-words">#{load.reference}</CardTitle>
                <Badge variant="accent">{load.status.replace(/_/g, " ")}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="flex min-w-0 items-start gap-1 break-words text-sm text-brand-600">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0" /> {load.origin} &rarr; {load.destination}
              </p>

              <div>
                <label className="mb-1 block text-sm font-medium text-brand-800">
                  Update status
                </label>
                <select
                  className="flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm"
                  value={load.status}
                  disabled={savingId === load.id}
                  onChange={(e) =>
                    void handleStatusChange(load.id, e.target.value as LoadRecord["status"])
                  }
                >
                  {DRIVER_STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              </div>

              <DocumentUploadCard
                title="Upload load document"
                description="Submit BOL or POD paperwork for staff review."
                relatedReference={load.reference}
                kinds={["bol", "pod", "other"]}
                defaultKind="bol"
                onUploaded={() => setDocumentsRefreshKey((key) => key + 1)}
              />
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-6">
        <MyDocumentsCard refreshKey={documentsRefreshKey} />
      </div>
    </>
  );
}
